// Topic Performance Service
// Tracks per-topic accuracy in Supabase and manages exam milestone logic.
// No OpenAI calls. Pure DB operations.

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import { WEAK_THRESHOLD } from '../config/recommendationConfig';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

// ─── Types ───────────────────────────────────────

export interface TopicPerformance {
  topic_id: string;
  attempts: number;
  correct: number;
  accuracy: number;
  last_practiced: string;
}

export interface ExamMeta {
  completed_test_count: number;
  last_exam_milestone: number;
  last_exam_question_ids: string[];
}

export interface PracticeResult {
  topicId: string;       // camelCase topic id (e.g. 'priorityRules')
  totalQuestions: number;
  correctAnswers: number;
}

// ─── Performance Tracking ────────────────────────

/**
 * Upserts a single topic's attempts/correct after a practice test finishes.
 * Best-score only: writes only if newAccuracy > existingAccuracy or no record exists.
 * Scores never go down; never penalise improvement by averaging.
 */
export async function updateTopicPerformance(
  userId: string,
  result: PracticeResult
): Promise<boolean> {
  try {
    const newAccuracy = Math.round((result.correctAnswers / result.totalQuestions) * 100);

    const { data: existing } = await supabase
      .from('user_topic_performance')
      .select('accuracy')
      .eq('user_id', userId)
      .eq('topic_id', result.topicId)
      .single();

    const existingAccuracy = existing?.accuracy ?? 0;
    if (newAccuracy <= existingAccuracy && existing) {
      return true; // Keep best score, do not overwrite
    }

    const { error } = await supabase
      .from('user_topic_performance')
      .upsert({
        user_id: userId,
        topic_id: result.topicId,
        attempts: result.totalQuestions,
        correct: result.correctAnswers,
        accuracy: newAccuracy,
        last_practiced: new Date().toISOString()
      }, { onConflict: 'user_id,topic_id' });

    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('updateTopicPerformance error:', error);
    return false;
  }
}

// ─── Read helpers ────────────────────────────────

/**
 * Returns all topic performance rows for a user, sorted by accuracy ASC (weakest first).
 */
export async function getAllTopicPerformance(
  userId: string
): Promise<TopicPerformance[]> {
  try {
    const { data, error } = await supabase
      .from('user_topic_performance')
      .select('topic_id, attempts, correct, accuracy, last_practiced')
      .eq('user_id', userId)
      .order('accuracy', { ascending: true });

    if (error) throw error;
    return (data || []) as TopicPerformance[];
  } catch (error) {
    logger.error('getAllTopicPerformance error:', error);
    return [];
  }
}

/**
 * Returns the N weakest topics for a user (accuracy < WEAK_THRESHOLD).
 */
export async function getWeakestTopics(
  userId: string,
  limit: number = 5
): Promise<TopicPerformance[]> {
  try {
    const { data, error } = await supabase
      .from('user_topic_performance')
      .select('topic_id, attempts, correct, accuracy, last_practiced')
      .eq('user_id', userId)
      .lt('accuracy', WEAK_THRESHOLD)
      .order('accuracy', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return (data || []) as TopicPerformance[];
  } catch (error) {
    logger.error('getWeakestTopics error:', error);
    return [];
  }
}

/**
 * Returns a summary object keyed by topic_id → accuracy.
 */
export async function getPerformanceProfile(
  userId: string
): Promise<Record<string, number>> {
  const rows = await getAllTopicPerformance(userId);
  const profile: Record<string, number> = {};
  for (const row of rows) {
    profile[row.topic_id] = row.accuracy;
  }
  return profile;
}

// ─── Exam Meta ───────────────────────────────────

/**
 * Returns the exam meta row for a user (creates one if missing).
 */
export async function getExamMeta(userId: string): Promise<ExamMeta> {
  try {
    const { data, error } = await supabase
      .from('user_exam_meta')
      .select('completed_test_count, last_exam_milestone, last_exam_question_ids')
      .eq('user_id', userId)
      .single();

    if (error && error.code === 'PGRST116') {
      // Row doesn't exist yet — return defaults
      return { completed_test_count: 0, last_exam_milestone: 0, last_exam_question_ids: [] };
    }
    if (error) throw error;

    return {
      completed_test_count: data.completed_test_count ?? 0,
      last_exam_milestone: data.last_exam_milestone ?? 0,
      last_exam_question_ids: data.last_exam_question_ids ?? []
    };
  } catch (error) {
    logger.error('getExamMeta error:', error);
    return { completed_test_count: 0, last_exam_milestone: 0, last_exam_question_ids: [] };
  }
}

/**
 * Increments completed_test_count by 1.
 * Returns the NEW count after increment.
 */
export async function incrementCompletedTestCount(userId: string): Promise<number> {
  try {
    const meta = await getExamMeta(userId);
    const newCount = meta.completed_test_count + 1;

    const { error } = await supabase
      .from('user_exam_meta')
      .upsert({
        user_id: userId,
        completed_test_count: newCount,
        last_exam_milestone: meta.last_exam_milestone,
        last_exam_question_ids: meta.last_exam_question_ids,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' });

    if (error) throw error;
    return newCount;
  } catch (error) {
    logger.error('incrementCompletedTestCount error:', error);
    return 0;
  }
}

/**
 * Records that an AI exam was generated at this milestone,
 * and stores the question IDs used (to avoid repeats).
 */
export async function recordExamMilestone(
  userId: string,
  milestone: number,
  questionIds: string[]
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_exam_meta')
      .upsert({
        user_id: userId,
        last_exam_milestone: milestone,
        last_exam_question_ids: questionIds,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' });

    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('recordExamMilestone error:', error);
    return false;
  }
}

/**
 * Returns the current completed_test_count for a user.
 */
export async function getCompletedTestCount(userId: string): Promise<number> {
  const meta = await getExamMeta(userId);
  return meta.completed_test_count;
}

/**
 * Returns consecutive days streak from user_topic_performance (last_practiced).
 * Counts days where at least 1 test was completed, ending today or yesterday.
 */
export async function getStreakCount(userId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('user_topic_performance')
      .select('last_practiced')
      .eq('user_id', userId)
      .order('last_practiced', { ascending: false });

    if (error) throw error;
    if (!data || data.length === 0) return 0;

    const uniqueDates = Array.from<string>(
      new Set(data.map((row) => new Date(row.last_practiced).toDateString()))
    ).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    if (uniqueDates[0] !== today && uniqueDates[0] !== yesterday) return 0;

    let streak = 0;
    for (let i = 0; i < uniqueDates.length; i++) {
      const expected = new Date(Date.now() - i * 86400000).toDateString();
      if (uniqueDates[i] === expected) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  } catch (error) {
    logger.error('getStreakCount error:', error);
    return 0;
  }
}
